import { type APIReferenceAttributes } from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import { type WorkspaceAndModelLookup } from '@ardoq/data-model';
import { scopeDataOperations } from '@ardoq/scope-data';
import {
  type IntendedRefTypes,
  isRefTypeViolation,
} from 'utils/metamodelConstraints';
import { type ReferenceComponents } from './referenceComponentsInterface';
import { getReferenceTypeNameFromModels } from './referenceTypeNameFromModels';

export type ReverseConstraintViolation = {
  isBlocked: boolean;
  isWarning: boolean;
  referenceTypeName: string | null;
  sourceName: string | null;
  sourceTypeName: string | null;
  targetName: string | null;
  targetTypeName: string | null;
};

type ReferenceData = {
  referenceTypeName: string | null;
  sourceTypeName: string | null;
  targetTypeName: string | null;
  sourceName: string | null;
  targetName: string | null;
};

const collectReferenceData = (
  reference: APIReferenceAttributes,
  { sourceComponentsById, targetComponentsById }: ReferenceComponents,
  workspaceAndModel: WorkspaceAndModelLookup
): ReferenceData => {
  const source = sourceComponentsById[reference.source];
  const target = targetComponentsById[reference.target];
  return {
    referenceTypeName: getReferenceTypeNameFromModels(
      reference,
      workspaceAndModel
    ),
    sourceTypeName: source
      ? (scopeDataOperations.getComponentTypeFromWorkspaceLookup(
          workspaceAndModel,
          source
        )?.name ?? null)
      : null,
    targetTypeName: target
      ? (scopeDataOperations.getComponentTypeFromWorkspaceLookup(
          workspaceAndModel,
          target
        )?.name ?? null)
      : null,
    sourceName: source?.name ?? null,
    targetName: target?.name ?? null,
  };
};

const evaluateConstraintViolation = (
  data: ReferenceData,
  intendedRefTypes: IntendedRefTypes | null,
  constraintEnforcementMode: string
): ReverseConstraintViolation | null => {
  const violates = isRefTypeViolation(
    intendedRefTypes,
    data.targetTypeName ?? '',
    data.sourceTypeName ?? '',
    data.referenceTypeName ?? ''
  );

  if (!violates) {
    return null;
  }

  return {
    isBlocked: constraintEnforcementMode === 'strict',
    isWarning: constraintEnforcementMode === 'warning',
    referenceTypeName: data.referenceTypeName,
    sourceName: data.sourceName,
    sourceTypeName: data.sourceTypeName,
    targetName: data.targetName,
    targetTypeName: data.targetTypeName,
  };
};

type GetReverseConstraintViolationsArgs = {
  referenceComponents: ReferenceComponents;
  workspaceAndModel: WorkspaceAndModelLookup;
  intendedRefTypes: IntendedRefTypes | null;
  constraintEnforcementMode: string;
};

export const getReverseConstraintViolations = ({
  referenceComponents,
  workspaceAndModel,
  intendedRefTypes,
  constraintEnforcementMode,
}: GetReverseConstraintViolationsArgs): ReverseConstraintViolation[] => {
  const isEnforcementActive =
    constraintEnforcementMode === 'warning' ||
    constraintEnforcementMode === 'strict';

  if (!isEnforcementActive) {
    return [];
  }

  return referenceComponents.references
    .map(reference =>
      collectReferenceData(reference, referenceComponents, workspaceAndModel)
    )
    .map(data =>
      evaluateConstraintViolation(
        data,
        intendedRefTypes,
        constraintEnforcementMode
      )
    )
    .filter(isTruthy);
};
